import React, { useState, useRef, useEffect } from 'react';
import { MessageSquare, Heart, MoreVertical, Pin, Share2, Flag, Edit2, Trash2, ChevronLeft, ChevronRight, Hash } from 'lucide-react';
import { timeAgo } from '../../../utils/timeAgo';
import { useTogglePin, useFlagDiscussion, useDeleteDiscussion } from '../hooks/use-panels';
import DiscussionComments from './DiscussionComments';
import CreateDiscussionModal from './CreateDiscussionModal';
import { useAuth } from '../../../contexts/AuthContext';
import CoFounderBadge from '../../../components/ui/CoFounderBadge';
import { isCoFounder } from '../../../utils/cofounderBadge';

const DiscussionCard = ({ discussion, onLike, panelId, defaultShowComments = false }) => {
  const { title, content, author, createdAt, _count, isPinned, isFlagged, imageUrl, images, tags = [] } = discussion;
  const displayImages = images?.length > 0 ? images : (imageUrl ? [imageUrl] : []);
  const commentCount = _count?.comments || 0;
  const likeCount = _count?.likes || 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const [shareToast, setShareToast] = useState(false);
  const [flagged, setFlagged] = useState(isFlagged || false);
  const [showComments, setShowComments] = useState(defaultShowComments);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isTextExpanded, setIsTextExpanded] = useState(defaultShowComments);
  const menuRef = useRef(null);

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === 0 ? displayImages.length - 1 : prev - 1));
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) => (prev === displayImages.length - 1 ? 0 : prev + 1));
  };

  // The carousel index is component state and survives a refetch, so removing an
  // image while the card is mounted leaves it pointing past the end of the list —
  // and no slide matches, so the carousel goes blank. Clamp on every render.
  const imageIndex = Math.min(currentImageIndex, Math.max(displayImages.length - 1, 0));

  const { user } = useAuth();
  const isOwner = (user?.id && (user.id === author?.id || user.id === discussion.authorId)) || (user?.name && (author?.name === user.name || !author?.name));

  const { mutate: togglePin, isPending: isPinning } = useTogglePin();
  const { mutate: flagDisc, isPending: isFlagging } = useFlagDiscussion();
  const { mutate: deleteDiscussion, isPending: isDeleting } = useDeleteDiscussion();

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate avatar initials
  const initials = (author?.name || 'U')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  // Deterministic soft color based on name
  const colors = [
    'bg-blue-100 text-blue-700',
    'bg-purple-100 text-purple-700',
    'bg-emerald-100 text-emerald-700',
    'bg-orange-100 text-orange-700',
    'bg-pink-100 text-pink-700',
    'bg-teal-100 text-teal-700'
  ];
  const colorIndex = (author?.name || '').length % colors.length;
  const avatarColor = colors[colorIndex];

  const handlePin = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    togglePin({ panelId, discussionId: discussion.id });
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    navigator.clipboard.writeText(window.location.href).then(() => {
      setShareToast(true);
      setTimeout(() => setShareToast(false), 2500);
    });
  };

  const handleFlag = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    flagDisc(
      { panelId, discussionId: discussion.id },
      { onSuccess: () => setFlagged(!flagged) }
    );
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowEditModal(true);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    deleteDiscussion({ panelId, discussionId: discussion.id });
    setShowDeleteConfirm(false);
  };

  return (
    <div
      className={`flex flex-col py-4 px-4 sm:px-5 relative min-w-0 ${
        isPinned ? 'bg-amber-50/20' : ''
      }`}
    >
      <div className="flex items-start gap-4 min-w-0">
      {/* Pin badge */}
      {isPinned && (
        <div className="absolute top-3 right-10 flex items-center gap-1 text-[10px] font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full">
          <Pin size={9} /> Pinned
        </div>
      )}

      {/* Avatar */}
      <div className={`w-10 h-10 rounded-full ${avatarColor} flex items-center justify-center text-sm font-semibold flex-shrink-0 overflow-hidden border border-indigo-100`}>
        {author?.avatarUrl || (isOwner && user?.avatarUrl) ? (
          <img src={author?.avatarUrl || user?.avatarUrl} alt={author?.name} className="w-full h-full object-cover" />
        ) : (
          initials
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top Author & Job Role Header */}
        <div className="flex items-center gap-2 flex-wrap mb-1.5">
          <span className="font-semibold text-gray-900 text-sm">
            {author?.role === 'student' || (isOwner && user?.role === 'student')
              ? (author?.name || (isOwner ? user?.name : 'Student'))
              : isCoFounder(author) || (isOwner && isCoFounder(user))
                ? (author?.name || (isOwner ? user?.name : 'Unknown'))
                : (author?.name ? (author?.name.startsWith('Dr.') ? author.name : `Dr. ${author.name}`) : (isOwner && user?.name ? `Dr. ${user.name}` : 'Unknown'))}
          </span>
          {(author?.jobRole || (isOwner && user?.jobRole) || author?.role === 'student' || (isOwner && user?.role === 'student')) && (
            <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full text-[10px] font-semibold border border-indigo-100">
              {author?.jobRole || (isOwner ? user?.jobRole : null) || (author?.role === 'student' || (isOwner && user?.role === 'student') ? 'Student' : (author?.specialization || 'Veterinarian'))}
            </span>
          )}
          <span className="text-gray-400 text-xs">• {timeAgo(createdAt)}</span>
        </div>

        <h4 
          onClick={() => setIsTextExpanded(!isTextExpanded)}
          className="text-[15px] font-semibold text-gray-900 leading-snug mb-1 break-words break-all [overflow-wrap:anywhere] cursor-pointer hover:text-[#6b46c1] transition-colors"
        >
          {title}
        </h4>
        <p 
          onClick={() => setIsTextExpanded(!isTextExpanded)}
          className={`text-sm text-gray-500 mb-2 whitespace-pre-wrap break-words break-all [overflow-wrap:anywhere] cursor-pointer ${!isTextExpanded ? 'line-clamp-2' : ''}`}
        >
          {content}
        </p>
        
        {content?.length > 40 && !defaultShowComments && (
          <button 
            onClick={(e) => { e.stopPropagation(); setIsTextExpanded(!isTextExpanded); }}
            className="text-xs font-semibold text-[#7367f0] hover:underline mb-2"
          >
            {isTextExpanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {tags.map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors text-xs font-medium rounded-md cursor-pointer"
              >
                <Hash size={11} className="text-blue-500" />
                {tag.replace(/^#/, '')}
              </span>
            ))}
          </div>
        )}
        
        {displayImages.length > 0 && (
          <div className="mb-3 relative rounded-xl overflow-hidden bg-black max-h-[400px]">
            {displayImages.map((img, i) => (
              <img
                key={i}
                src={img}
                alt=""
                className={`w-full max-h-[400px] object-contain transition-opacity duration-200 ${i === imageIndex ? 'block' : 'hidden'}`}
              />
            ))}
            
            {displayImages.length > 1 && (
              <>
                <button 
                  onClick={prevImage}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <button 
                  onClick={nextImage}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                  {displayImages.map((_, i) => (
                    <div 
                      key={i} 
                      className={`h-1.5 w-1.5 rounded-full transition-colors ${i === imageIndex ? 'bg-white' : 'bg-white/40'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        
        <div className="flex items-center gap-4 text-xs text-gray-400 mt-2">
          <button 
            onClick={() => setShowComments(!showComments)}
            className={`flex items-center gap-1.5 hover:text-[#6b46c1] transition-colors font-medium ${showComments ? 'text-[#6b46c1]' : ''}`}
          >
            <MessageSquare size={13} /> {commentCount} Comments
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onLike && onLike(discussion); }}
            className="flex items-center gap-1.5 hover:text-red-400 transition-colors font-medium"
          >
            <Heart size={13} /> {likeCount} Likes
          </button>
          {flagged && (
            <span className="flex items-center gap-1 text-red-400 text-[11px]">
              <Flag size={11} /> Flagged
            </span>
          )}
        </div>
      </div>

      {/* Menu */}
      <div className="relative flex-shrink-0" ref={menuRef}>
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen((o) => !o); }}
          className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
        >
          <MoreVertical size={16} />
        </button>

        {/* Dropdown */}
        {menuOpen && (
          <div
            className="absolute right-0 top-8 z-30 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 w-48 animate-in fade-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Pin */}
            <button
              onClick={handlePin}
              disabled={isPinning}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Pin size={15} className={isPinned ? 'text-amber-500' : 'text-gray-400'} />
              <span className="font-medium">{isPinned ? 'Unpin Discussion' : 'Pin Discussion'}</span>
            </button>

            {/* Share */}
            <button
              onClick={handleShare}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Share2 size={15} className="text-gray-400" />
              <span className="font-medium">Share</span>
            </button>

            {/* Divider */}
            <div className="h-px bg-gray-100 my-1" />

            {/* Flag */}
            <button
              onClick={handleFlag}
              disabled={isFlagging}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              <Flag size={15} className="text-red-400" />
              <span className="font-medium text-red-500">
                {flagged ? 'Unflag Discussion' : 'Flag as Irrelevant'}
              </span>
            </button>

            {/* Owner Actions */}
            {isOwner && (
              <>
                <div className="h-px bg-gray-100 my-1" />
                <button
                  onClick={handleEditClick}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Edit2 size={15} className="text-gray-400" />
                  <span className="font-medium">Edit Discussion</span>
                </button>
                <button
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 size={15} className="text-red-400" />
                  <span className="font-medium text-red-500">Delete Discussion</span>
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Share toast */}
      {shareToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-xl shadow-lg animate-in fade-in slide-in-from-bottom-2 duration-200">
          🔗 Link copied to clipboard!
        </div>
      )}
      </div>

      {/* Inline Comments Drawer */}
      {showComments && (
        <DiscussionComments panelId={panelId} discussionId={discussion.id} />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <CreateDiscussionModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          panelId={panelId}
          initialData={discussion}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Discussion</h3>
              <p className="text-sm text-gray-500">
                Are you sure you want to delete this discussion? All comments and likes will be permanently removed.
              </p>
            </div>
            <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-100">
              <button
                onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm(false); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 bg-gray-100 rounded-lg transition-colors"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); confirmDelete(); }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscussionCard;
